// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

/* eslint-disable spire/export-styles */
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { screen } from "@rugby/XDL/Helpers";
import * as React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => ({
    pageTitle: getCurrentPage(state).fields.title,
});

// <XNG-Changes>
const enum fields {
    showTitle = "showTitle",
    customTitle = "customTitle",
    subTitle = "subTitle",
    showSubTitle = "showSubTitle",
}

interface FieldsProps extends WidgetProps {
    fields: {
        [fields.customTitle]: string;
        [fields.showTitle]: boolean;
        [fields.subTitle]: string;
        [fields.showSubTitle]: boolean;
    };
}

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & FieldsProps;

export interface TitleStyles {
    wrapper?: InjectableCss;
    justifiedWrapper?: InjectableCss;
    titleText?: TypographyProps;
    subTitleText?: TypographyProps;
}

// It's a custom style from XNG
export const titleStyles: TitleStyles = {
    wrapper: {
        css: css`
            display: flex;
            flex-wrap: wrap;
            align-items: flex-end;
            margin: 50px 0 40px 0;
            ${screen("lte", "sm")`
                margin: 0 0 20px 0;
            `};
        `,
    },
    justifiedWrapper: {
        css: css`
            display: flex;
            flex-wrap: wrap;
            justify-content: space-between;
            align-items: flex-end;
            margin: 50px 0 40px 0;
            ${screen("lte", "sm")`
                margin: 6px 0 0 0;
            `};
        `,
    },
    titleText: {
        size: 42,
        css: css`
            letter-spacing: 0;
            line-height: 50px;
            margin: 0;
            ${screen("lte", "sm")`
                font-size: 32px;
            `};
        `,
    },
    subTitleText: {
        size: 22,
        css: css`
            letter-spacing: 0;
            line-height: 26px;

            // to make span on the same line
            margin-left: 10px;
            margin-bottom: 5px;
        `,
    },
};

// </XNG-Changes>

const styles = titleStyles;

// <XNG-Changes>
const PageTitle: React.FunctionComponent<Props> = ({ pageTitle, fields }: Props) => {
    const title = fields.showTitle ? fields.customTitle : pageTitle;
    return (
        <StyledWrapper {...styles.wrapper}>
            <Typography variant="h2" as="h1" data-test-selector="PageTitle" {...styles.titleText}>
                {title}
            </Typography>
            {fields.showSubTitle && (
                <Typography data-test-selector="PageTitle_subtitle" {...styles.subTitleText}>
                    {fields.subTitle}
                </Typography>
            )}
        </StyledWrapper>
    );
};
// </XNG-Changes>

// <XNG-Changes>
const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(PageTitle),
    definition: {
        group: "Basic",
        icon: "heading",
        fieldDefinitions: [
            {
                name: fields.showTitle,
                displayName: "Custom Title",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
            {
                name: fields.customTitle,
                displayName: "Custom Title",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
                isVisible: item => item.fields.showTitle,
                validate: value => (value.length > 0 ? null : "Please enter a title."),
            },
            {
                name: fields.showSubTitle,
                displayName: "Subtitle Title",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
            {
                name: fields.subTitle,
                displayName: "Subtitle Title",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
                isVisible: item => item.fields.showSubTitle,
                isRequired: true,
                validate: value => (value.length > 0 ? null : "Please enter a SubTitle."),
            },
        ],
    },
};
// </XNG-Changes>

export default widgetModule;
